import React, { useEffect, useId, useState } from 'react'
import { serverMessage } from '../error/server-message'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

// Whether an organization allows AI tools usage data (owner decision
// 2026-10-23). A person's choice to share follows them into every
// organization; an organization that turns this off has nothing from work
// done in it stored by EducationXR, even for people who chose to share, and
// their data from other organizations is unaffected. On by default.
//
// One switch, two homes: the organization's own page (its admins see it, its
// owners change it: read / save are getOwnSetting / setOwnSetting) and the
// organization's system page (staff: getOrgSetting / setOrgSetting bound to
// its id). The caller passes the reads and says whether this viewer may
// change it; whyNot is the sentence shown when they may not.
export const AI_TOOLS_SHARING_LABEL = 'Allow AI tools usage data'
export const AI_TOOLS_SHARING_HELP = 'When off, nothing from work in this organization is shared with EducationXR, even for people who chose to share.'

export const AIToolsSharing = ({ load, save, canUpdate, whyNot, children }) => {
  const id = useId()
  const [allowed, setAllowed] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    Promise.resolve().then(() => load()).then(
      s => { if (live) { setError(null); setAllowed(s && typeof s.aiToolsTelemetryAllowed === 'boolean' ? s.aiToolsTelemetryAllowed : null) } },
      err => { if (live) { setAllowed(null); setError(serverMessage(err, "This organization's AI tools setting couldn't be loaded. Try again in a moment."), err) } }
    )
    return () => { live = false }
  }, [load, setError])

  const change = async () => {
    if (busy || !canUpdate) return
    const next = !allowed
    setBusy(true)
    setSaveError(null)
    try {
      const answer = await save(next)
      setAllowed(answer && typeof answer.aiToolsTelemetryAllowed === 'boolean' ? answer.aiToolsTelemetryAllowed : next)
    } catch (err) {
      setSaveError(serverMessage(err, "The AI tools setting couldn't be changed. Try again in a moment."), err)
    }
    setBusy(false)
  }

  return (
    <div className="ai-tools-sharing" data-testid="ai-tools-sharing">
      {error && <p className="text-danger small mb-1" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} />}
      {allowed === undefined && !error && <p className="text-body-secondary small mb-0" role="status">Loading…</p>}
      {typeof allowed === 'boolean' &&
        <>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id={id} checked={allowed} disabled={!canUpdate || busy} onChange={change} aria-describedby={`${id}-help`} />
            <label className="form-check-label" htmlFor={id}>{AI_TOOLS_SHARING_LABEL}</label>
          </div>
          <p className="text-body-secondary small mt-1 mb-0" id={`${id}-help`}>{AI_TOOLS_SHARING_HELP}</p>
          {!canUpdate && whyNot && <p className="sw-hint small mt-1 mb-0">{whyNot}</p>}
          {saveError && <p className="text-danger small mt-1 mb-1" role="alert">{saveError}</p>}
          {saveError && <ErrorReference error={saveFailure} />}
        </>}
      {children}
    </div>
  )
}
